import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight, Calendar, CircleCheck, Clock3, Copy,
  FileText, Link2, Mail, MapPin, PencilLine, PhoneCall, PlayCircle,
  PlusSquare, ReceiptText, RefreshCw, TriangleAlert, UserRound, Video,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import DealHeader from '@/components/deal/DealHeader';
import ProposalDeliveryEstimateCard from '@/components/deal/ProposalDeliveryEstimateCard';
import ProposalStatusBadge from '@/components/deal/ProposalStatusBadge';
import CreativePreviewFallback from '@/components/deal/CreativePreviewFallback';
import useAuthenticatedMediaUrl from '@/hooks/useAuthenticatedMediaUrl';
import useDealFlow from '@/hooks/useDealFlow';
import { videoApiClient } from '@/services/videoApi';
import {
  DEAL_STAGES_V2,
  FOLLOW_UP_OPTIONS,
  PROPOSAL_STATUSES,
  formatDollars,
  getCurrentCreativeForBusiness,
  getLatestProposalForBusiness,
  getProposalTimeline,
  isMockGenerationEnabled,
} from '@/components/deal/mockDealData';

function normalizePreviewMediaUrl(url) {
  if (typeof url !== 'string' || !url) return '';
  if (
    url.startsWith('http://')
    || url.startsWith('https://')
    || url.startsWith('blob:')
    || url.startsWith('data:')
  ) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${videoApiClient.baseUrl}${url}`;
  }
  return url;
}

function buildTimestampPatch(status) {
  const now = new Date().toISOString();
  switch (status) {
    case 'sent':
      return { status, sent_at: now, accepted_at: null, changes_requested_at: null, lost_at: null };
    case 'accepted':
      return { status, accepted_at: now };
    case 'changes_requested':
      return { status, changes_requested_at: now };
    case 'expired':
      return { status, expired_at: now };
    case 'lost':
      return { status, lost_at: now };
    case 'draft':
    default:
      return { status };
  }
}

function formatTimelineDate(timestamp, options = {}) {
  if (!timestamp) return null;
  return new Date(timestamp).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  });
}

function getFollowUpMeta(sentAt, followUpDays) {
  if (!sentAt || !followUpDays) return null;

  const sentDate = new Date(sentAt);
  const dueDate = new Date(sentDate);
  dueDate.setDate(dueDate.getDate() + Number(followUpDays));

  const now = new Date();
  const todayLabel = now.toDateString();
  const dueLabel = dueDate.toDateString();
  const isOverdue = dueDate < now && dueLabel !== todayLabel;
  const isToday = dueLabel === todayLabel;

  return {
    date: dueDate,
    label: isToday ? 'Today' : dueDate.toLocaleDateString([], { month: 'short', day: 'numeric' }),
    isOverdue,
    isToday,
  };
}

async function copyTextToClipboard(value) {
  if (!value) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // fall through
  }

  try {
    const helper = document.createElement('textarea');
    helper.value = value;
    helper.setAttribute('readonly', '');
    helper.style.position = 'absolute';
    helper.style.left = '-9999px';
    document.body.appendChild(helper);
    helper.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(helper);
    return copied;
  } catch {
    return false;
  }
}

function buildMailtoUrl({ recipientEmail, subject, body }) {
  const address = String(recipientEmail || '').trim();
  const encodedSubject = encodeURIComponent(subject || '');
  const encodedBody = encodeURIComponent(body || '');
  return `mailto:${address}?subject=${encodedSubject}&body=${encodedBody}`;
}

function openMailtoUrl(mailtoUrl) {
  if (typeof document !== 'undefined') {
    const anchor = document.createElement('a');
    anchor.href = mailtoUrl;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    return;
  }

  if (typeof window !== 'undefined') {
    window.location.assign(mailtoUrl);
  }
}

async function copyWithFeedback(value, successMessage, failureMessage) {
  const didCopy = await copyTextToClipboard(value);
  if (didCopy) {
    toast.success(successMessage);
    return true;
  }
  toast.error(failureMessage);
  return false;
}

export default function ProposalBuilder() {
  const { orgSlug, dealId } = useParams();
  const navigate = useNavigate();
  const deal = useDealFlow();
  const mockGenerationEnabled = isMockGenerationEnabled();
  const businessId = deal.activeBusinessId || deal.businessProfile?.id;
  const businessProposals = businessId ? (deal.proposals?.[businessId] || []) : [];
  const latestBusinessProposal = useMemo(
    () => getLatestProposalForBusiness(businessProposals),
    [businessProposals],
  );
  const proposal = deal.proposal || latestBusinessProposal;
  const creativePreview = useMemo(
    () => getCurrentCreativeForBusiness(deal, businessId, { mockGenerationEnabled }),
    [deal, businessId, mockGenerationEnabled],
  );
  const [pitchMessage, setPitchMessage] = useState('');
  const [method, setMethod] = useState('link');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [followUp, setFollowUp] = useState('2');
  const [copied, setCopied] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailAssistOpen, setEmailAssistOpen] = useState(false);
  const [emailAssistDraft, setEmailAssistDraft] = useState(null);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [proposalLoadError, setProposalLoadError] = useState(null);
  const [proposalPreviewFailed, setProposalPreviewFailed] = useState(false);
  const copiedTimeoutRef = useRef(null);
  const previewAsset = useMemo(
    () => creativePreview.creative?.assets?.find((asset) => asset.type === 'video' && asset.url)
      || creativePreview.creative?.assets?.[0]
      || null,
    [creativePreview.creative],
  );
  const previewVideoUrl = useMemo(
    () => normalizePreviewMediaUrl(
      previewAsset?.url
      || creativePreview.creative?.videoUrl
      || creativePreview.creative?.video_url
      || creativePreview.creative?.previewUrl
      || creativePreview.creative?.preview_url
      || creativePreview.creative?.shareUrl
      || creativePreview.creative?.share_url
      || '',
    ),
    [creativePreview.creative, previewAsset],
  );
  const previewPosterUrl = useMemo(
    () => normalizePreviewMediaUrl(
      creativePreview.thumbnailUrl
      || creativePreview.creative?.thumbnailUrl
      || creativePreview.creative?.thumbnail_url
      || previewAsset?.thumbnail_url
      || '',
    ),
    [creativePreview.thumbnailUrl, creativePreview.creative, previewAsset],
  );
  const {
    resolvedUrl: playablePreviewVideoUrl,
    isLoading: isLoadingPreviewVideo,
  } = useAuthenticatedMediaUrl(previewVideoUrl);
  const hasVideoPreview = Boolean(playablePreviewVideoUrl);

  useEffect(() => () => {
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
  }, []);

  useEffect(() => {
    setProposalPreviewFailed(false);
  }, [previewPosterUrl]);

  useEffect(() => {
    deal.setCurrentStage('proposal');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!businessId || proposal) return;
    let cancelled = false;

    setProposalLoading(true);
    setProposalLoadError(null);
    deal.ensureDraftProposalForBusiness(businessId).catch((error) => {
      if (!cancelled) {
        console.error('Failed to ensure proposal draft', error);
        setProposalLoadError(error);
      }
    }).finally(() => {
      if (!cancelled) {
        setProposalLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [businessId, deal.ensureDraftProposalForBusiness, proposal]);

  useEffect(() => {
    if (!proposal) return;
    setPitchMessage(proposal.pitch_message || '');
    setMethod(proposal.deliveryMethod || 'link');
    setContactName(proposal.contact?.name || '');
    setEmail(proposal.recipientEmail || proposal.contact?.email || '');
    setFollowUp(String(proposal.followUpDays ?? 2));
  }, [proposal]);

  if (proposalLoading && !proposal) {
    return null;
  }

  if (!proposal) {
    if (proposalLoadError) {
      return (
        <>
          <DealHeader
            businessName={deal.businessProfile?.name}
            currentStage="proposal"
            stages={DEAL_STAGES_V2}
            onBack={() => navigate(-1)}
          />

          <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <h1 className="text-2xl font-semibold text-slate-950">Proposal workspace</h1>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  {proposalLoadError.message || 'A proposal could not be created from the current workspace state.'}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/org/${orgSlug}/deal/${dealId}/offer`)}
                    className="rounded-xl bg-white"
                  >
                    Open offer
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/org/${orgSlug}/deal/${dealId}/pitch`)}
                    className="rounded-xl bg-white"
                  >
                    Open creative
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      );
    }
    return null;
  }

  const sharePublicUrl = proposal.publicUrl || proposal.activeShareLink?.publicUrl || '';
  const statusConfig = PROPOSAL_STATUSES[proposal.status] || PROPOSAL_STATUSES.draft;
  const isEditable = ['draft', 'changes_requested'].includes(proposal.status);
  const isAwaitingBuyer = proposal.status === 'sent';
  const isCommerciallyLocked = proposal.status === 'accepted'
    && deal.agreement?.payment_method_status === 'confirmed';
  const flightLabel = proposal.flight?.label || `${proposal.flight?.weeks || deal.selectedPackage?.flight || 2} weeks`;
  const targetLabel = proposal.target_area?.label || deal.selectedPackage?.targetAddress || (deal.businessProfile?.name ? `Around ${deal.businessProfile.name}` : 'Not set');
  const pricing = proposal.pricing || deal.selectedPackage?.pricing || null;
  const timeline = getProposalTimeline(proposal, deal.agreement);
  const sentLabel = formatTimelineDate(proposal.sent_at, { hour: 'numeric', minute: '2-digit' });
  const followUpMeta = getFollowUpMeta(proposal.sent_at, proposal.followUpDays);
  const buyerStatusTitle = 'Proposal is with the buyer';
  const buyerStatusSummary = 'The proposal has been sent and is waiting on buyer review or a commercial response.';
  const nextStepLabel = followUpMeta?.isOverdue
    ? 'Follow up now'
    : followUpMeta?.isToday
      ? 'Follow up today'
      : followUpMeta
        ? `Follow up by ${followUpMeta.label}`
        : 'Wait for buyer response';

  const persistProposal = (nextProposal) => {
    const normalized = { ...nextProposal, updated_at: nextProposal.updated_at || new Date().toISOString() };
    deal.setProposal(normalized);
    if (businessId) {
      deal.upsertProposalToBusiness(businessId, normalized);
      if (normalized.creativeId) {
        deal.linkProposalToCreative(businessId, normalized.creativeId, normalized.id);
      }
    }
    return normalized;
  };

  const makeProposal = (overrides = {}) => {
    const now = new Date().toISOString();
    const next = {
      ...proposal,
      packageName: deal.selectedPackage?.name || proposal.packageName,
      package_id: deal.selectedPackage?.id || proposal.package_id,
      budget_cap: deal.selectedPackage?.budget || proposal.budget_cap,
      price: deal.selectedPackage?.pricing?.sellPrice || deal.selectedPackage?.budget || proposal.price,
      pricing: deal.selectedPackage?.pricing || proposal.pricing,
      flight: {
        ...proposal.flight,
        weeks: deal.selectedPackage?.duration?.weeks || deal.selectedPackage?.flight || proposal.flight?.weeks || 2,
        label: deal.selectedPackage?.duration?.label || proposal.flight?.label || `${deal.selectedPackage?.flight || proposal.flight?.weeks || 2} weeks`,
      },
      target_area: {
        type: deal.selectedPackage?.targetArea || proposal.target_area?.type || 'around_location',
        label: deal.selectedPackage?.targetAddress || proposal.target_area?.label || (deal.businessProfile?.name ? `Around ${deal.businessProfile.name}` : 'Not set'),
      },
      creative_set_id: deal.creativeSet?.id || proposal.creative_set_id || null,
      creativeId: deal.selectedCreativeId || proposal.creativeId || creativePreview.creative?.id || null,
      pitch_message: pitchMessage.trim(),
      followUpDays: followUp === 'none' ? null : Number(followUp),
      deliveryMethod: method,
      recipientEmail: email.trim(),
      contact: {
        name: contactName.trim(),
        email: email.trim(),
      },
      updated_at: now,
      ...overrides,
    };

    deal.setScope({
      ...(deal.scope || {}),
      contact: next.contact,
    });

    deal.setSendConfig({
      method: next.deliveryMethod,
      recipients: next.deliveryMethod === 'email' && next.recipientEmail ? [next.recipientEmail] : [],
      followUpDays: next.followUpDays,
    });

    return next;
  };

  const prepareProposalForSharing = async () => {
    if (!pitchMessage.trim()) {
      throw new Error('Add a proposal message before sharing.');
    }
    if (!businessId) {
      throw new Error('Business workspace unavailable.');
    }

    if (proposal?.id && !isEditable) {
      return proposal;
    }

    const nextProposal = makeProposal();
    if (proposal?.id) {
      return deal.patchProposalForBusiness(businessId, nextProposal);
    }

    return deal.ensureDraftProposalForBusiness(businessId, {
      package_name: nextProposal.packageName || deal.selectedPackage?.name || null,
      pitch_message: nextProposal.pitch_message || '',
      delivery_method: nextProposal.deliveryMethod || 'link',
      contact_json: nextProposal.contact || null,
      follow_up_days: nextProposal.followUpDays ?? null,
    });
  };

  const markCopied = () => {
    setCopied(true);
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyShareLink = async () => {
    setShareLoading(true);
    try {
      const currentProposal = await prepareProposalForSharing();
      const sharedProposal = await deal.shareProposalForBusiness(businessId, currentProposal.id);
      const nextPublicUrl = sharedProposal.publicUrl || sharedProposal.activeShareLink?.publicUrl || '';
      if (!nextPublicUrl) {
        throw new Error('The share link is not available yet.');
      }

      // Mirror the email path: clicking "Copy share link" is the seller's act
      // of delivery, so transition the proposal to "sent" before attempting
      // the clipboard copy. If we did this after, a clipboard-blocked browser
      // would leave the proposal stuck in "draft" with no buyer-response
      // controls (Update buyer response, Create revision) reachable.
      if (sharedProposal.status !== 'sent') {
        try {
          await deal.sendProposalForBusiness(businessId, sharedProposal);
        } catch (sendError) {
          console.warn('Failed to mark proposal as sent after share', sendError);
        }
      }

      const didCopy = await copyTextToClipboard(nextPublicUrl);
      if (!didCopy) {
        toast.warning('Share link generated. Copy was blocked — select the link field to copy manually.');
        return;
      }

      markCopied();
      toast.success('Share link copied');
    } catch (error) {
      console.error('Failed to copy proposal share link', error);
      toast.error(error?.message || 'Could not generate a share link');
    } finally {
      setShareLoading(false);
    }
  };

  const handleSaveDraft = () => {
    if (!pitchMessage.trim()) {
      toast.error('Add a proposal message before saving.');
      return;
    }
    const nextProposal = makeProposal(buildTimestampPatch('draft'));
    if (!businessId) {
      persistProposal(nextProposal);
      toast.success('Proposal draft saved');
      return;
    }

    deal.patchProposalForBusiness(businessId, nextProposal).then(() => {
      toast.success('Proposal draft saved');
    }).catch((error) => {
      console.error('Failed to save proposal draft', error);
      toast.error('Could not save the proposal draft');
    });
  };

  const handleEmailProposal = async () => {
    if (!pitchMessage.trim()) {
      toast.error('Add a proposal message before emailing.');
      return;
    }
    if (!email.trim()) {
      toast.error('Enter a recipient email before sending.');
      return;
    }

    setMethod('email');
    setEmailLoading(true);
    try {
      if (!businessId) {
        throw new Error('Business workspace unavailable.');
      }

      const nextProposal = makeProposal({ deliveryMethod: 'email' });
      const sentProposal = await deal.sendProposalForBusiness(businessId, nextProposal);
      const recipientEmail = sentProposal.recipientEmail || email.trim();
      const subject = sentProposal.emailSubject || '';
      const bodyText = sentProposal.emailBodyText || '';

      if (!recipientEmail) {
        throw new Error('The backend did not return a recipient email.');
      }

      const mailtoUrl = buildMailtoUrl({
        recipientEmail,
        subject,
        body: bodyText,
      });
      setEmailAssistDraft({
        recipientEmail,
        subject,
        bodyText,
        shareUrl: sentProposal.publicUrl || sentProposal.activeShareLink?.publicUrl || '',
      });
      setEmailAssistOpen(true);
      openMailtoUrl(mailtoUrl);
      toast.success('Your email draft is ready. If your email app did not open, use the copy actions in the dialog.');
    } catch (error) {
      console.error('Failed to send proposal', error);
      toast.error(error?.message || 'Could not send the proposal');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleRecordStatus = (status) => {
    if (!businessId) {
      const next = persistProposal(makeProposal(buildTimestampPatch(status)));
      if (status === 'accepted') {
        toast.success('Proposal marked accepted');
        navigate(`/org/${orgSlug}/deal/${dealId}/close`);
        return;
      }
      toast.success(`Proposal marked ${String(status).replace(/_/g, ' ')}`);
      deal.setProposal(next);
      return;
    }

    deal.recordProposalResponseForBusiness(businessId, proposal.id, status).then((next) => {
      if (status === 'accepted') {
        toast.success('Proposal marked accepted');
        navigate(`/org/${orgSlug}/deal/${dealId}/close`);
        return;
      }
      toast.success(`Proposal marked ${String(status).replace(/_/g, ' ')}`);
      deal.setProposal(next);
    }).catch((error) => {
      console.error('Failed to record proposal response', error);
      toast.error('Could not update buyer response');
    });
  };

  const handleCreateRevision = () => {
    if (!businessId) {
      const revised = makeProposal({
        id: `prop-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        status: 'draft',
        version: (proposal.version || 1) + 1,
        previousVersionId: proposal.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        sent_at: null,
        accepted_at: null,
        changes_requested_at: null,
        expired_at: null,
        lost_at: null,
      });
      persistProposal(revised);
      toast.success('Revision draft created');
      return;
    }

    deal.createProposalRevisionForBusiness(businessId, proposal.id).then((revised) => {
      deal.setProposal(revised);
      toast.success('Revision draft created');
    }).catch((error) => {
      console.error('Failed to create proposal revision', error);
      toast.error('Could not create a revision');
    });
  };

  return (
    <>
      <DealHeader
        businessName={deal.businessProfile?.name}
        currentStage="proposal"
        stages={DEAL_STAGES_V2}
        onBack={() => navigate(-1)}
      />

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Badge variant="outline" className={`rounded-full text-xs ${statusConfig.className}`}>
              {statusConfig.label}
            </Badge>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
              {isEditable ? 'Proposal workspace' : 'Proposal record'}
            </h1>
            {isCommerciallyLocked && (
              <p className="mt-2 text-sm font-medium text-emerald-700">
                This proposal is locked because billing has been confirmed. Use a revision if commercial terms need to change.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {isAwaitingBuyer ? (
              <Button variant="outline" onClick={handleCopyShareLink} disabled={shareLoading} className="rounded-xl bg-white">
                {shareLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : copied ? <CircleCheck className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                Copy share link
              </Button>
            ) : !isCommerciallyLocked && (
              <>
                <Button variant="outline" onClick={() => navigate(`/org/${orgSlug}/deal/${dealId}/offer`)} className="rounded-xl bg-white">
                  <PencilLine className="h-4 w-4" />
                  Edit offer
                </Button>
                <Button variant="outline" onClick={() => navigate(`/org/${orgSlug}/deal/${dealId}/pitch`)} className="rounded-xl bg-white">
                  <Video className="h-4 w-4" />
                  {creativePreview.type === 'none' ? 'Add creative' : 'Open creative'}
                </Button>
              </>
            )}
            {isCommerciallyLocked && (
              <Button variant="outline" onClick={handleCreateRevision} className="rounded-xl bg-white">
                <PlusSquare className="h-4 w-4" />
                Clone as revision
              </Button>
            )}
          </div>
        </div>

        <Dialog open={responseDialogOpen} onOpenChange={setResponseDialogOpen}>
          <DialogContent className="rounded-3xl border-slate-200 p-0 sm:max-w-xl">
            <DialogHeader className="border-b border-slate-100 px-6 pt-6 pb-5 text-left">
              <DialogTitle className="text-2xl font-semibold text-slate-950">Update buyer response</DialogTitle>
              <DialogDescription className="text-sm leading-6 text-slate-600">
                Record the latest buyer decision for this proposal. This updates the proposal status and keeps the workflow moving.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 px-6 py-5">
              <button
                type="button"
                onClick={() => {
                  setResponseDialogOpen(false);
                  handleRecordStatus('accepted');
                }}
                className="w-full rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-100/70"
              >
                <div className="flex items-start gap-3">
                  <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                  <div>
                    <p className="text-sm font-semibold text-slate-950">Accepted</p>
                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      The buyer is ready to move forward. Continue into agreement and billing.
                    </p>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setResponseDialogOpen(false);
                  handleRecordStatus('changes_requested');
                }}
                className="w-full rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left transition-colors hover:border-amber-300 hover:bg-amber-100/70"
              >
                <div className="flex items-start gap-3">
                  <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
                  <div>
                    <p className="text-sm font-semibold text-slate-950">Changes requested</p>
                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      The buyer wants adjustments. This keeps the current version on record and shifts the next step to revision.
                    </p>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setResponseDialogOpen(false);
                  handleRecordStatus('lost');
                }}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left transition-colors hover:border-slate-300 hover:bg-slate-100"
              >
                <div className="flex items-start gap-3">
                  <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
                  <div>
                    <p className="text-sm font-semibold text-slate-950">Not moving forward</p>
                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      Close this proposal without billing or launch, while keeping the record for future follow-up.
                    </p>
                  </div>
                </div>
              </button>
            </div>

            <DialogFooter className="border-t border-slate-100 px-6 py-4 sm:justify-start">
              <Button variant="outline" onClick={() => setResponseDialogOpen(false)} className="rounded-xl bg-white">
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={emailAssistOpen} onOpenChange={setEmailAssistOpen}>
          <DialogContent className="flex max-h-[calc(100vh-1rem)] w-[calc(100vw-1rem)] max-w-2xl flex-col overflow-hidden rounded-3xl border-slate-200 p-0 sm:max-h-[90vh] sm:w-full">
            <DialogHeader className="shrink-0 border-b border-slate-100 px-4 pt-5 pb-4 text-left sm:px-6 sm:pt-6 sm:pb-5">
              <DialogTitle className="text-2xl font-semibold text-slate-950">Email draft ready</DialogTitle>
              <DialogDescription className="text-sm leading-6 text-slate-600">
                We prepared the proposal email. If your email app did not open automatically, use these details to send it manually.
              </DialogDescription>
            </DialogHeader>

            <div className="min-h-0 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5">
              <div className="space-y-4">
                <div className="rounded-2xl border border-sky-200 bg-sky-50/80 p-4">
                  <p className="text-sm font-semibold text-slate-950">What to do next</p>
                  <ol className="mt-3 space-y-2 text-sm leading-6 text-slate-700">
                    <li>1. Click <span className="font-medium text-slate-950">Open email app again</span> one more time if you want to retry the automatic handoff.</li>
                    <li>2. If nothing opens, start a new email in Gmail, Outlook, or Apple Mail.</li>
                    <li>3. Copy the recipient, subject, and message below into that email.</li>
                  </ol>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <Label className="text-xs text-slate-500">Recipient</Label>
                    <Button
                      variant="outline"
                      className="h-10 w-full shrink-0 rounded-xl bg-white sm:w-auto"
                      onClick={async () => {
                        await copyWithFeedback(
                          emailAssistDraft?.recipientEmail || '',
                          'Recipient copied',
                          'Could not copy the recipient email',
                        );
                      }}
                    >
                      <Copy className="h-4 w-4" />
                      Copy recipient
                    </Button>
                  </div>
                  <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-tight whitespace-normal break-all text-slate-900">
                    {emailAssistDraft?.recipientEmail || 'No recipient email returned'}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <Label className="text-xs text-slate-500">Subject</Label>
                    <Button
                      variant="outline"
                      className="h-10 w-full shrink-0 rounded-xl bg-white sm:w-auto"
                      onClick={async () => {
                        await copyWithFeedback(
                          emailAssistDraft?.subject || '',
                          'Subject copied',
                          'Could not copy the email subject',
                        );
                      }}
                    >
                      <Copy className="h-4 w-4" />
                      Copy subject
                    </Button>
                  </div>
                  <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 [overflow-wrap:anywhere]">
                    {emailAssistDraft?.subject || 'No subject returned'}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <Label className="text-xs text-slate-500">Email message</Label>
                    <Button
                      variant="outline"
                      className="h-10 w-full shrink-0 rounded-xl bg-white sm:w-auto"
                      onClick={async () => {
                        await copyWithFeedback(
                          emailAssistDraft?.bodyText || '',
                          'Email message copied',
                          'Could not copy the email message',
                        );
                      }}
                    >
                      <Copy className="h-4 w-4" />
                      Copy message
                    </Button>
                  </div>
                  <div className="mt-3 max-h-[260px] overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 whitespace-pre-wrap text-slate-700 [overflow-wrap:anywhere]">
                    {emailAssistDraft?.bodyText || 'No email message returned'}
                  </div>
                </div>

                {emailAssistDraft?.shareUrl ? (
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <Label className="text-xs text-slate-500">Share link</Label>
                      <Button
                        variant="outline"
                        className="h-10 w-full shrink-0 rounded-xl bg-white sm:w-auto"
                        onClick={async () => {
                          await copyWithFeedback(
                            emailAssistDraft.shareUrl,
                            'Share link copied',
                            'Could not copy the share link',
                          );
                        }}
                      >
                        <Copy className="h-4 w-4" />
                        Copy share link
                      </Button>
                    </div>
                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 break-all">
                      {emailAssistDraft.shareUrl}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <DialogFooter className="shrink-0 flex-col-reverse gap-2 border-t border-slate-100 px-4 py-4 sm:flex-row sm:justify-between sm:px-6">
              <Button variant="outline" onClick={() => setEmailAssistOpen(false)} className="w-full rounded-xl bg-white sm:w-auto">
                Close
              </Button>
              <Button
                onClick={() => {
                  const mailtoUrl = buildMailtoUrl({
                    recipientEmail: emailAssistDraft?.recipientEmail || '',
                    subject: emailAssistDraft?.subject || '',
                    body: emailAssistDraft?.bodyText || '',
                  });
                  openMailtoUrl(mailtoUrl);
                }}
                className="w-full rounded-xl bg-coral text-white hover:bg-coral/90 sm:w-auto"
              >
                <Mail className="h-4 w-4" />
                Open email app again
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="grid gap-6 xl:grid-cols-[1.45fr_0.95fr]">
          <div className="space-y-6">
            <Card className="overflow-hidden border-slate-200 pt-0 shadow-sm">
              <CardContent className="p-0">
                <div className="aspect-video bg-slate-950 relative">
                  {hasVideoPreview ? (
                    <video
                      src={playablePreviewVideoUrl}
                      poster={previewPosterUrl || undefined}
                      controls
                      playsInline
                      className="h-full w-full object-cover"
                    />
                  ) : previewPosterUrl && !proposalPreviewFailed ? (
                    <img
                      src={previewPosterUrl}
                      alt={creativePreview.label}
                      className="h-full w-full object-cover opacity-85"
                      onError={() => setProposalPreviewFailed(true)}
                    />
                  ) : (
                    <CreativePreviewFallback
                      label={creativePreview.type === 'none' ? 'Preview unavailable' : 'Preview rendering'}
                    />
                  )}
                  {isLoadingPreviewVideo && !hasVideoPreview ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-950/60 backdrop-blur-[1px]">
                      <div className="flex items-center gap-2 rounded-full bg-white/92 px-4 py-2 text-sm font-medium text-slate-950 shadow-sm">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Loading preview
                      </div>
                    </div>
                  ) : null}
                  {!hasVideoPreview ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 shadow-sm">
                        <PlayCircle className="h-7 w-7 text-slate-900" />
                      </div>
                    </div>
                  ) : null}
                  <Badge className="absolute top-4 left-4 border-0 bg-black/55 text-white">
                    {creativePreview.isMock ? 'Mock preview' : creativePreview.type === 'none' ? 'No creative attached' : 'Creative preview'}
                  </Badge>
                </div>

                <div className="space-y-5 p-6">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <p className="text-sm font-medium uppercase tracking-[0.16em] text-slate-500">Buyer-facing proposal</p>
                      <h2 className="mt-1 text-2xl font-bold text-slate-950">{proposal.packageName}</h2>
                      <p className="mt-2 text-sm text-slate-600">Prepared for {deal.businessProfile?.name || 'this business'} · v{proposal.version || 1}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-5">
                    <div>
                      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                        <ReceiptText className="h-3.5 w-3.5 text-emerald-600" />
                        Sell Price
                      </div>
                      <p className="mt-1 text-xl font-semibold text-slate-950">{formatDollars(proposal.price)}</p>
                    </div>
                    <div className="h-8 w-px bg-slate-200" />
                    <div>
                      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                        <Calendar className="h-3.5 w-3.5 text-blue-600" />
                        Flight
                      </div>
                      <p className="mt-1 text-xl font-semibold text-slate-950">{flightLabel}</p>
                    </div>
                    <div className="h-8 w-px bg-slate-200" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                        <MapPin className="h-3.5 w-3.5 text-coral" />
                        Targeting
                      </div>
                      <p className="mt-1 text-sm font-semibold text-slate-950 truncate">{targetLabel}</p>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-slate-950">Proposal message</h3>
                      {proposal.previousVersionId && (
                        <Badge variant="outline" className="rounded-full bg-slate-50 text-xs text-slate-600">
                          Revision of {proposal.previousVersionId}
                        </Badge>
                      )}
                    </div>
                    {isEditable ? (
                      <Textarea
                        value={pitchMessage}
                        onChange={(event) => setPitchMessage(event.target.value)}
                        className="min-h-[170px] rounded-2xl border-slate-200 bg-slate-50 text-sm"
                      />
                    ) : (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm leading-6 whitespace-pre-wrap text-slate-700">
                        {proposal.pitch_message}
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <h3 className="text-sm font-semibold text-slate-950">What the buyer should expect</h3>
                    <div className="mt-4 space-y-3">
                      {[
                        'Review the proposal scope and investment summary.',
                        'Confirm the commercial fit and request changes if needed.',
                        'Approve the proposal and complete billing setup.',
                        'Move into launch once blockers are cleared.',
                      ].map((step) => (
                        <div key={step} className="flex items-start gap-3 text-sm text-slate-600">
                          <CircleCheck className="mt-0.5 h-4 w-4 text-teal" />
                          <span>{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <ProposalDeliveryEstimateCard deliveryEstimate={proposal.deliveryEstimate} />

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-base font-semibold text-slate-950">Proposal timeline</h3>
                  <ProposalStatusBadge status={proposal.status} />
                </div>
                <div className="mt-5 space-y-4">
                  {timeline.length > 0 ? timeline.map((event) => (
                    <div key={event.key} className="flex items-start gap-3">
                      <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-950">{event.label}</p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {new Date(event.timestamp).toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  )) : (
                    <p className="text-sm text-slate-500">
                      Timeline events will appear as the proposal moves through buyer review.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-950">{isAwaitingBuyer ? 'Buyer status' : 'Seller controls'}</h3>
                  <ProposalStatusBadge status={proposal.status} />
                </div>

                <div className="mt-5 space-y-4">
                  {isAwaitingBuyer ? (
                    <>
                      <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Buyer status</p>
                        <h4 className="mt-3 text-lg font-semibold text-slate-950">{buyerStatusTitle}</h4>
                        <p className="mt-1 text-sm leading-6 text-slate-600">{buyerStatusSummary}</p>

                        <div className="mt-4 grid gap-3 sm:grid-cols-2">
                          <div className="rounded-xl border border-white/80 bg-white px-3 py-3">
                            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Sent via</p>
                            <p className="mt-1 text-sm font-semibold text-slate-950">{method === 'email' ? 'Email' : 'Share link'}</p>
                          </div>
                          <div className="rounded-xl border border-white/80 bg-white px-3 py-3">
                            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Recipient</p>
                            <p className="mt-1 text-sm font-semibold break-all text-slate-950">
                              {contactName || email || 'No buyer contact recorded'}
                            </p>
                          </div>
                          <div className="rounded-xl border border-white/80 bg-white px-3 py-3">
                            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Sent</p>
                            <p className="mt-1 text-sm font-semibold text-slate-950">{sentLabel || 'Not recorded'}</p>
                          </div>
                          <div className="rounded-xl border border-white/80 bg-white px-3 py-3">
                            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Follow-up</p>
                            <p className={`mt-1 text-sm font-semibold ${followUpMeta?.isOverdue ? 'text-amber-700' : 'text-slate-950'}`}>
                              {followUpMeta ? `${followUpMeta.isOverdue ? 'Overdue · ' : ''}${followUpMeta.label}` : 'Not scheduled'}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 p-4">
                        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Recommended next step</p>
                        <p className="mt-3 text-lg font-semibold text-slate-950">{nextStepLabel}</p>
                        <p className="mt-1 text-sm leading-6 text-slate-600">
                          {followUpMeta?.isOverdue
                            ? 'The reminder window has passed. Use follow-up to capture a clear yes, no, or revision request.'
                            : 'Keep this version unchanged while the buyer reviews it, then update the response when you hear back.'}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-slate-200 p-4">
                        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Public proposal link</p>
                        <div className="mt-3 flex gap-2">
                          <Input
                            value={sharePublicUrl}
                            readOnly
                            placeholder="Share link will appear here"
                            className="rounded-xl border-slate-200 bg-slate-50 text-sm"
                          />
                          <Button variant="outline" onClick={handleCopyShareLink} disabled={shareLoading} className="rounded-xl bg-white">
                            {shareLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : copied ? <CircleCheck className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Button onClick={() => setResponseDialogOpen(true)} className="h-11 w-full rounded-xl bg-coral text-white hover:bg-coral/90">
                          Update buyer response
                          <ArrowRight className="h-4 w-4" />
                        </Button>

                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" onClick={handleCreateRevision} className="rounded-xl bg-white">
                            <PlusSquare className="h-4 w-4" />
                            Create revision
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="rounded-2xl border border-slate-200 p-4">
                        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Send proposal</p>

                        <div className="mt-3 space-y-3">
                          <div>
                            <Label className="text-xs text-slate-500">Public proposal link</Label>
                            <div className="mt-1 flex gap-2">
                              <Input
                                value={sharePublicUrl}
                                readOnly
                                placeholder="Generate a share link when you are ready"
                                className="rounded-xl border-slate-200 bg-slate-50 text-sm"
                              />
                              <Button variant="outline" onClick={handleCopyShareLink} disabled={shareLoading} className="rounded-xl bg-white">
                                {shareLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : copied ? <CircleCheck className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                              </Button>
                            </div>
                          </div>

                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <Label className="text-xs text-slate-500">Contact name</Label>
                              <Input
                                value={contactName}
                                disabled={!isEditable}
                                onChange={(event) => setContactName(event.target.value)}
                                className="mt-1 rounded-xl border-slate-200 bg-slate-50"
                                placeholder="Primary buyer"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">Recipient email</Label>
                              <Input
                                value={email}
                                disabled={!isEditable}
                                onChange={(event) => setEmail(event.target.value)}
                                className="mt-1 rounded-xl border-slate-200 bg-slate-50"
                                placeholder="owner@business.com"
                              />
                            </div>
                          </div>

                          <div>
                            <Label className="text-xs text-slate-500">Follow-up reminder</Label>
                            <Select value={followUp} onValueChange={setFollowUp} disabled={!isEditable}>
                              <SelectTrigger className="mt-1 rounded-xl border-slate-200 bg-slate-50">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {FOLLOW_UP_OPTIONS.map((option) => (
                                  <SelectItem key={option.days ?? 'none'} value={String(option.days ?? 'none')}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 p-4">
                        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Internal economics</p>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          <div className="rounded-xl bg-slate-50 p-3">
                            <p className="text-xs text-slate-500">Sell price</p>
                            <p className="mt-1 text-lg font-semibold text-slate-950">{formatDollars(proposal.price)}</p>
                          </div>
                          <div className="rounded-xl bg-slate-50 p-3">
                            <p className="text-xs text-slate-500">Seller earnings</p>
                            <p className="mt-1 text-lg font-semibold text-teal">
                              {formatDollars(pricing?.sellerEarnings || 0)}
                            </p>
                          </div>
                        </div>
                        {pricing && (
                          <p className="mt-3 text-sm text-slate-600">
                            Platform cost {formatDollars(pricing.platformCost)} with {Math.round((pricing.marginPct || 0) * 100)}% seller margin.
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col gap-3">
                        {isEditable ? (
                          <>
                            <Button variant="outline" onClick={handleSaveDraft} className="h-11 rounded-xl bg-white">
                              <FileText className="h-4 w-4" />
                              Save draft
                            </Button>
                            <Button onClick={handleCopyShareLink} disabled={shareLoading} className="h-11 rounded-xl bg-coral text-white hover:bg-coral/90">
                              {shareLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
                              Copy share link
                            </Button>
                            <Button variant="outline" onClick={handleEmailProposal} disabled={emailLoading || !email.trim()} className="h-10 rounded-xl bg-white text-sm font-medium">
                              {emailLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                              Or send by email
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button variant="outline" onClick={() => navigate(-1)} className="h-11 rounded-xl bg-white">
                              Back
                            </Button>
                            {proposal.status === 'accepted' && !isCommerciallyLocked ? (
                              <Button onClick={() => navigate(`/org/${orgSlug}/deal/${dealId}/close`)} className="h-11 rounded-xl bg-coral text-white hover:bg-coral/90">
                                Continue to billing
                                <ArrowRight className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button variant="outline" onClick={handleCreateRevision} className="h-11 rounded-xl bg-white">
                                <PencilLine className="h-4 w-4" />
                                {isCommerciallyLocked ? 'Clone as revision' : 'Create revision'}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-2">
                  {creativePreview.type === 'none' ? (
                    <TriangleAlert className="h-4 w-4 text-amber-600" />
                  ) : creativePreview.isMock ? (
                    <PhoneCall className="h-4 w-4 text-blue-600" />
                  ) : (
                    <UserRound className="h-4 w-4 text-slate-700" />
                  )}
                  <h3 className="text-base font-semibold text-slate-950">Creative attachment</h3>
                </div>
                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-medium text-slate-950">{creativePreview.label}</p>
                  <p className="mt-1 text-sm text-slate-600">{creativePreview.detail}</p>
                  {creativePreview.type === 'none' && (
                    <Button variant="outline" size="sm" onClick={() => navigate(`/org/${orgSlug}/deal/${dealId}/pitch`)} className="mt-3 rounded-xl bg-white">
                      <Video className="h-4 w-4" />
                      Generate creative
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
